/**
 * /partner/listings/new/:assetType    → blank wizard
 * /partner/listings/:assetType/:id    → edit existing
 *
 * Standalone luxury page hosting the unified PartnerListingWizard. Includes
 * a top back-link and an asset-type chooser when none is selected.
 */
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Home, Car, Ship, Palette } from 'lucide-react';
import Nav from '@/components/Nav';
import PartnerListingWizard from '@/components/PartnerListingWizard';
import { useAuth } from '@/context/AuthContext';

const ASSET_OPTIONS = [
  { type: 'property', label: 'Property', Icon: Home, copy: 'Apartments, villas, plots, commercial real estate' },
  { type: 'car', label: 'Luxury Car', Icon: Car, copy: 'Pre-owned and new luxury automobiles' },
  { type: 'yacht', label: 'Yacht', Icon: Ship, copy: 'Charter or sale, motor or sailing' },
  { type: 'interior_design', label: 'Interior Design', Icon: Palette, copy: 'Residential, commercial & hospitality projects' },
];

const PartnerListingPage = () => {
  const { assetType, listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      <Nav variant="partner" showBack partnerLabel="Partner Portal" companyName={user?.company_name} />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-gold text-sm mb-6"
          data-testid="partner-listing-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {!assetType ? (
          <div className="space-y-8">
            <header>
              <h1 className="font-playfair text-3xl md:text-4xl font-semibold mb-2">Create a New Listing</h1>
              <p className="text-text-secondary text-sm">Choose the asset type to begin. All listings are bound to your partner country and currency.</p>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ASSET_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  onClick={() => navigate(`/partner/listings/new/${opt.type}`)}
                  className="glass-card p-6 text-left hover:border-gold/40 transition-all group"
                  data-testid={`asset-type-${opt.type}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center flex-shrink-0">
                      <opt.Icon className="w-5 h-5 text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg group-hover:text-gold transition-colors">{opt.label}</h3>
                      <p className="text-text-secondary text-sm mt-1">{opt.copy}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <header className="mb-6">
              <h1 className="font-playfair text-2xl md:text-3xl font-semibold">
                {listingId ? 'Edit Listing' : 'New Listing'}
              </h1>
              <p className="text-text-secondary text-sm mt-1">
                Complete every section to publish a premium-quality listing.
              </p>
            </header>
            <PartnerListingWizard
              assetType={assetType}
              listingId={listingId}
              onSaved={() => {}}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerListingPage;
